import React, { useState, useRef, useEffect } from "react";
import { vimeoBgUrl } from "@/lib/vimeo";

/**
 * VimeoBg
 *   - default mode: scales 100% to parent container (cards, sections)
 *   - hero mode: viewport-sized 16:9 cover (full-bleed hero only)
 */
export function VimeoBg({ id, eager = false, hero = false, className = "", title = "Background video" }) {
  const [shouldLoad, setShouldLoad] = useState(eager);
  const ref = useRef(null);

  useEffect(() => {
    if (eager || shouldLoad) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShouldLoad(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [eager, shouldLoad]);

  const cls = hero ? "vimeo-cover-hero" : "vimeo-cover";
  return (
    <div ref={ref} className={`${cls} ${className}`}>
      {shouldLoad && (
        <iframe
          title={title}
          src={vimeoBgUrl(id)}
          allow="autoplay; fullscreen; picture-in-picture"
          loading="lazy"
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export function VimeoFrame({ id, className = "", title = "Featured project video" }) {
  return (
    <div className={`relative w-full ${className}`} style={{ aspectRatio: "16 / 9" }}>
      <iframe
        title={title}
        src={vimeoBgUrl(id, { background: 1 })}
        allow="autoplay; fullscreen; picture-in-picture"
        className="absolute inset-0 w-full h-full"
        loading="lazy"
      />
    </div>
  );
}

/**
 * HoverVideo — mounts the Vimeo player on FIRST hover only. Unmounts 3
 * seconds after hover exits so we don't accumulate ghost streams when the
 * user has hovered many cards (each Vimeo iframe = ~500KB player JS + video
 * segments still buffering even when `opacity:0`).
 *
 * The 3s grace period keeps re-hover snappy without paying the always-mounted
 * bandwidth cost.
 */
export function HoverVideo({ id, active, overlay = "bg-ink/10" }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (active) {
      setMounted(true);
      return;
    }
    // Hover exited — tear down the iframe after a short grace window
    const t = setTimeout(() => setMounted(false), 3000);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <div className={`absolute inset-0 transition-opacity duration-500 ${active ? "opacity-100" : "opacity-0"}`}>
      {mounted && <VimeoBg id={id} eager />}
      <div className={`absolute inset-0 ${overlay}`} />
    </div>
  );
}

export default VimeoBg;
